import React from 'react';
import Modal from './Modal';
import Button from './Button';

/**
 * ConfirmDialog Component
 * Beautiful confirmation dialog with customizable styling
 */

const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger', // 'danger', 'warning', 'info', 'success'
  icon,
}) => {
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  // Variant styles
  const variantStyles = {
    danger: {
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      confirmButton: 'danger',
      defaultIcon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      ),
    },
    warning: {
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      confirmButton: 'primary',
      defaultIcon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      ),
    },
    info: {
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      confirmButton: 'primary',
      defaultIcon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    success: {
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      confirmButton: 'success',
      defaultIcon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
  };

  const style = variantStyles[variant];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      showCloseButton={false}
      closeOnOverlayClick={false}
    >
      <div className="text-center">
        {/* Icon */}
        <div className="mx-auto flex items-center justify-center w-14 h-14 rounded-full mb-4">
          <div className={`w-14 h-14 rounded-full ${style.iconBg} flex items-center justify-center`}>
            <div className={style.iconColor}>
              {icon || style.defaultIcon}
            </div>
          </div>
        </div>

        {/* Title */}
        <h3 className="text-lg font-semibold text-neutral-900 mb-2">
          {title}
        </h3>

        {/* Message */}
        {message && (
          <p className="text-sm text-neutral-600 mb-6">
            {message}
          </p>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-center">
          <Button
            variant="ghost"
            onClick={onClose}
            className="min-w-[100px]"
          >
            {cancelText}
          </Button>
          <Button
            variant={style.confirmButton}
            onClick={handleConfirm}
            className="min-w-[100px]"
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmDialog;

/**
 * Usage Examples:
 *
 * // Delete Confirmation
 * <ConfirmDialog
 *   isOpen={showDeleteConfirm}
 *   onClose={() => setShowDeleteConfirm(false)}
 *   onConfirm={handleDelete}
 *   title="Delete Activity?"
 *   message="Are you sure you want to delete this activity? This action cannot be undone."
 *   confirmText="Delete"
 *   cancelText="Cancel"
 *   variant="danger"
 * />
 *
 * // Warning Confirmation
 * <ConfirmDialog
 *   isOpen={showWarning}
 *   onClose={() => setShowWarning(false)}
 *   onConfirm={handleProceed}
 *   title="Unsaved Changes"
 *   message="You have unsaved changes. Do you want to proceed?"
 *   variant="warning"
 * />
 *
 * // Info Confirmation
 * <ConfirmDialog
 *   isOpen={showInfo}
 *   onClose={() => setShowInfo(false)}
 *   onConfirm={handleContinue}
 *   title="Continue?"
 *   message="This will start the session."
 *   variant="info"
 * />
 */
